/**
 * A signed-in wallet's dashboard: the campaigns it takes part in as a creator and the campaigns
 * it runs as a brand, each with what it needs from the wallet next. A wallet that only runs
 * campaigns sees its brand side first.
 */

import { Megaphone, Send } from "lucide-react";
import { Link } from "react-router";

import { PLATFORM_DETAILS, shownHandle, useWalletAccounts } from "../accounts/accounts.js";
import { type CampaignPhase, campaignPath } from "../campaigns/campaigns.js";
import { requestNextStep } from "../campaigns/next-step.js";
import { PhaseBadge } from "../campaigns/PhaseBadge.js";
import type { CampaignRequest } from "../campaigns/requests.js";
import { useMyRequests, useOfferedRequests } from "../campaigns/requests.js";
import { StatusBadge } from "../campaigns/StatusBadge.js";
import {
  checkProblems,
  inReview,
  rejectionText,
  useAllMySubmissions,
  type WalletSubmission,
} from "../campaigns/submissions.js";
import { RequireWallet } from "../components/RequireWallet.js";
import {
  Badge,
  EmptyState,
  ErrorMessage,
  Loading,
  Notice,
  PageHeader,
  Section,
  Stat,
  styles,
} from "../components/ui.js";
import { formatUtc, formatWindow } from "../format.js";
import { formatTotal, usePayouts } from "../payouts/payouts.js";
import { platformLabel } from "./campaigns/RequestList.js";

export function Dashboard() {
  return (
    <RequireWallet reason="Your dashboard shows the campaigns you take part in and the campaigns you run.">
      <DashboardView />
    </RequireWallet>
  );
}

function DashboardView() {
  const submissions = useAllMySubmissions();
  const requests = useMyRequests();
  const brandFirst = (requests.data?.length ?? 0) > 0 && submissions.data?.length === 0;

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Dashboard"
        title="Your campaigns"
        documentTitle="Dashboard"
        actions={
          <>
            <Link to="/campaigns" className={styles.secondaryButton}>
              Browse campaigns
            </Link>
            <Link to="/campaigns/new" className={styles.primaryButton}>
              Start a campaign
            </Link>
          </>
        }
      >
        The campaigns you take part in as a creator and those you run as a brand, with what each
        needs from you next.
      </PageHeader>
      {/* Keyed, so that changing the order moves the panels instead of mounting them anew. */}
      <div className="mt-8 space-y-8">
        {brandFirst
          ? [<BrandPanel key="brand" />, <CreatorPanel key="creator" />]
          : [<CreatorPanel key="creator" />, <BrandPanel key="brand" />]}
      </div>
    </div>
  );
}

function CreatorPanel() {
  const accounts = useWalletAccounts();
  const submissions = useAllMySubmissions();
  const payouts = usePayouts();

  const linked = accounts.data?.accounts ?? [];
  // Accepted submissions take part. Withdrawn and refused ones do not.
  const taking = submissions.data?.filter((entry) => entry.status === "accepted") ?? [];
  const takingIn = groupByCampaign(taking).length;
  const campaigns = groupByCampaign(submissions.data ?? []);
  const unclaimed = (payouts.data?.payouts ?? []).filter((payout) => payout.claimed === null);

  return (
    <Section
      title="As a creator"
      description="Your linked accounts, what you submitted and what you can claim."
    >
      <dl className="grid gap-3 sm:grid-cols-3">
        <Stat
          label="Linked accounts"
          value={
            accounts.isPending
              ? "…"
              : linked.length > 0
                ? linked.map((account) => shownHandle(account)).join(", ")
                : "None yet"
          }
          hint={
            linked.length > 0
              ? linked.map((account) => PLATFORM_DETAILS[account.platform].name).join(", ")
              : undefined
          }
        />
        <Stat
          label="Submissions taking part"
          value={submissions.isPending ? "…" : taking.length}
          hint={takingIn > 0 ? `In ${takingIn} campaign${takingIn === 1 ? "" : "s"}` : undefined}
        />
        <Stat
          label="To claim"
          value={payouts.isPending ? "…" : formatTotal(unclaimed)}
          hint={
            unclaimed.length > 0
              ? `${unclaimed.length} payout${unclaimed.length === 1 ? "" : "s"} waiting`
              : undefined
          }
        />
      </dl>

      {accounts.isSuccess && linked.length === 0 && (
        <Notice tone="info" title="Link an account to take part" className="mt-4">
          Morcast measures your posts through your linked X account or YouTube channel.{" "}
          <Link to="/accounts" className={styles.link}>
            Link an account
          </Link>
        </Notice>
      )}
      {unclaimed.length > 0 && (
        <Notice tone="success" title="You have earnings to claim" className="mt-4">
          <Link to="/payouts" className={styles.link}>
            Claim your payouts
          </Link>{" "}
          whenever you like. They never expire.
        </Notice>
      )}

      <h3 className="mt-6 text-sm font-semibold">Your submissions</h3>
      <div className="mt-3">
        {submissions.isPending && <Loading />}
        {submissions.isError && <ErrorMessage error={submissions.error} />}
        {submissions.data?.length === 0 && (
          <EmptyState
            icon={Send}
            title="You have not submitted anything yet"
            action={
              <Link to="/campaigns" className={styles.primaryButton}>
                Browse campaigns
              </Link>
            }
          >
            Pick an open campaign, publish content that follows its brief, and submit the link while
            the campaign runs.
          </EmptyState>
        )}
        {campaigns.length > 0 && (
          <ul className="space-y-3">
            {campaigns.map((group) => (
              <CampaignSubmissions
                key={`${group.campaign.escrow}/${group.campaign.id}`}
                {...group}
              />
            ))}
          </ul>
        )}
      </div>
    </Section>
  );
}

/** A campaign the wallet submitted to: where it stands, and each submission's state. */
function CampaignSubmissions({
  campaign,
  submissions,
}: {
  campaign: WalletSubmission["campaign"];
  submissions: WalletSubmission[];
}) {
  return (
    <li className="rounded-xl p-4 ring-1 ring-zinc-200 dark:ring-white/10">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <Link to={campaignPath(campaign)} className="font-semibold hover:underline">
            {campaign.title}
          </Link>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            {campaign.brand} · {platformLabel(campaign)}
          </p>
        </div>
        <PhaseBadge phase={campaign.phase} />
      </div>
      <ul className="mt-3 divide-y divide-zinc-200 text-sm dark:divide-white/10">
        {submissions.map((submission) => (
          <SubmissionLine key={submission.id} submission={submission} />
        ))}
      </ul>
      <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
        {nextForCreator(campaign.phase, campaign)}
      </p>
    </li>
  );
}

function SubmissionLine({ submission }: { submission: WalletSubmission }) {
  const problems = submission.status === "accepted" ? checkProblems(submission) : [];
  const reviewing = submission.status === "accepted" && inReview(submission);
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
      <a href={submission.url} className="min-w-0 break-all hover:underline">
        {submission.url}
      </a>
      {submission.status === "accepted" && <Badge tone="green">Accepted</Badge>}
      {submission.status === "withdrawn" && <Badge tone="grey">Withdrawn</Badge>}
      {submission.status === "rejected" && <Badge tone="red">Not accepted</Badge>}
      <span className="text-xs text-zinc-500 dark:text-zinc-400">
        {formatUtc(submission.receivedAt)}
      </span>
      {submission.status === "rejected" && (
        <p className="basis-full text-zinc-600 dark:text-zinc-400">
          {rejectionText(submission.reason, submission.campaign.platform)}
        </p>
      )}
      {problems.length > 0 && (
        <p className="basis-full text-amber-700 dark:text-amber-300/90">
          This will fail verification. {problems.join(" ")}
        </p>
      )}
      {reviewing && problems.length === 0 && (
        <p className="basis-full text-zinc-600 dark:text-zinc-400">
          A person at Morcast is checking part of it.
        </p>
      )}
    </li>
  );
}

function BrandPanel() {
  const requests = useMyRequests();
  const offered = useOfferedRequests();
  const all = requests.data ?? [];
  const count = (status: CampaignRequest["status"]) =>
    all.filter((request) => request.status === status).length;
  // Campaigns whose budget is in the escrow: not yet settled, cancelled or refunded.
  const funded = all.filter((request) => request.campaign?.status === "funded").length;

  return (
    <Section
      title="As a brand"
      description="Your campaign requests and the campaigns they became."
      actions={
        all.length > 0 && (
          <Link to="/campaigns/requests" className={styles.ghostButton}>
            All requests
          </Link>
        )
      }
    >
      <dl className="grid gap-3 sm:grid-cols-3">
        <Stat label="Waiting for review" value={requests.isPending ? "…" : count("submitted")} />
        <Stat label="Awaiting your deposit" value={requests.isPending ? "…" : count("accepted")} />
        <Stat label="Funded campaigns" value={requests.isPending ? "…" : funded} />
      </dl>
      {offered.data && offered.data.length > 0 && (
        <Notice
          tone="info"
          title={
            offered.data.length === 1
              ? "A campaign request is offered to this wallet"
              : `${offered.data.length} campaign requests are offered to this wallet`
          }
          className="mt-4"
        >
          Another wallet offers you its request to take over and deposit.{" "}
          <Link to="/campaigns/requests" className={styles.link}>
            Review the offer
          </Link>
        </Notice>
      )}
      <div className="mt-5">
        {requests.isPending && <Loading />}
        {requests.isError && <ErrorMessage error={requests.error} />}
        {requests.data?.length === 0 && (
          <EmptyState
            icon={Megaphone}
            title="You have not requested a campaign yet"
            action={
              <Link to="/campaigns/new" className={styles.primaryButton}>
                Start a campaign
              </Link>
            }
          >
            Describe your campaign and its budget. Morcast reviews it and sends you the deposit
            details. You only pay for verified organic reach.
          </EmptyState>
        )}
        {all.length > 0 && (
          <ul className="divide-y divide-zinc-200 dark:divide-white/10">
            {all.slice(0, 5).map((request) => (
              <li key={request.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-3">
                <Link
                  to={`/campaigns/requests/${request.id}`}
                  className="font-medium hover:underline"
                >
                  {request.brief.title}
                </Link>
                <StatusBadge request={request} />
                <span className="basis-full text-sm text-zinc-600 dark:text-zinc-400">
                  {requestNextStep(request)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Section>
  );
}

/** The wallet's submissions grouped by campaign, in the order of their newest submission. */
function groupByCampaign(submissions: WalletSubmission[]) {
  const groups = new Map<
    string,
    { campaign: WalletSubmission["campaign"]; submissions: WalletSubmission[] }
  >();
  for (const submission of submissions) {
    const key = `${submission.campaign.escrow}/${submission.campaign.id}`;
    const group = groups.get(key);
    if (group) {
      group.submissions.push(submission);
    } else {
      groups.set(key, { campaign: submission.campaign, submissions: [submission] });
    }
  }
  return [...groups.values()];
}

/** What happens next in a campaign the wallet took part in. */
function nextForCreator(phase: CampaignPhase, campaign: WalletSubmission["campaign"]): string {
  switch (phase) {
    case "upcoming":
    case "running":
      return `Open from ${formatWindow(campaign.platform, campaign.startAt, campaign.endAt)}. Results are measured after the end.`;
    case "ended":
      return "Ended. Morcast measures and checks the results, then publishes them on the campaign page for review before the settlement.";
    case "settled":
      return "Settled. Any payout you earned is on your Payouts page.";
    case "refunded":
      return "Refunded. The budget went back to the brand.";
  }
}
